import React, { useState, useRef } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import IconButton from '@mui/joy/IconButton';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import { themeColors, shadows } from '../../styles/theme';

export default function ImageUpload({
  value,
  onChange,
  label = 'Image',
  accept = 'image/*',
  previewSize = 80,
  required = false,
  error = '',
  helperText = ''
}) {
  const [preview, setPreview] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;

    if (!file.type.match('image.*')) {
      return;
    }

    onChange(file);

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.match('image.*'));

    if (imageFile) {
      handleFileSelect(imageFile);
    }
  };

  return (
    <FormControl required={required} error={!!error}>
      <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
        {label}
        {required && (
          <Typography component="span" sx={{ color: themeColors.danger, ml: 0.5 }}>
            *
          </Typography>
        )}
      </FormLabel>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {preview ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: previewSize,
                height: previewSize,
                borderRadius: 2.5,
                overflow: 'hidden',
                border: 'none',
                boxShadow: shadows.md
              }}
            >
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
            <IconButton
              size="sm"
              onClick={handleRemove}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                borderRadius: '50%',
                minHeight: 28,
                minWidth: 28,
                backgroundColor: themeColors.danger,
                color: 'white',
                border: 'none',
                boxShadow: shadows.md,
                '&:hover': {
                  backgroundColor: `${themeColors.danger}DD`,
                  boxShadow: shadows.lg,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              width: previewSize,
              height: previewSize,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isDragging ? `2px solid ${themeColors.primary[500]}` : `2px dashed ${themeColors.neutral[300]}`,
              backgroundColor: isDragging ? `${themeColors.primary[500]}10` : themeColors.neutral[50],
              boxShadow: isDragging ? shadows.focus : shadows.sm,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: themeColors.primary[400],
                backgroundColor: `${themeColors.primary[500]}05`,
                boxShadow: shadows.md,
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <PhotoCameraIcon sx={{ color: themeColors.neutral[400], fontSize: 32 }} />
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          <Button
            component="label"
            startDecorator={<CloudUploadIcon />}
            size="md"
            sx={{
              border: 'none',
              boxShadow: shadows.sm,
              borderRadius: 2,
              backgroundColor: preview ? 'white' : themeColors.primary[500],
              color: preview ? themeColors.neutral[700] : 'white',
              fontWeight: 600,
              '&:hover': {
                boxShadow: shadows.md,
                backgroundColor: preview ? themeColors.neutral[50] : themeColors.primary[600],
              },
              transition: 'all 0.2s ease',
            }}
          >
            {preview ? 'Change Image' : `Upload ${label}`}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={accept}
              onChange={handleInputChange}
            />
          </Button>

          <Typography level="body-xs" sx={{ mt: 1, color: themeColors.neutral[500] }}>
            Drag and drop or click to upload
          </Typography>
        </Box>
      </Box>

      {error && (
        <FormHelperText sx={{ color: themeColors.danger, fontSize: '0.875rem', mt: 0.75 }}>
          {error}
        </FormHelperText>
      )}
      {helperText && !error && (
        <FormHelperText sx={{ color: themeColors.neutral[600], fontSize: '0.875rem', mt: 0.75 }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}